import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

export interface IBrand {
    id?: number;
    avatarUrl: string;
    name: string;
}

export const fetchBrands = createAsyncThunk("brand/fetchBrands", async (restricted: boolean, { rejectWithValue }) => {
    try {
        const params = restricted !== undefined ? { restricted } : {};
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/company/brands`, { params });
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const postBrand = createAsyncThunk(
    "brand/postBrand",
    async ({ data }: { data: IBrand }, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/company/brands`, data);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const putBrand = createAsyncThunk(
    "brand/putBrand",
    async ({ data, id }: { data: IBrand; id: number }, { rejectWithValue }) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/company/brands/${id}`, data);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteBrand = createAsyncThunk(
    "brand/deleteBrand",
    async ({ id }: { id: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/company/brands/${id}`);
            return id;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export interface BrandState {
    brands: IBrand[];
}

const initialState: BrandState = {
    brands: [],
};

export const brandSlice = createSlice({
    name: "brand",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchBrands.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchBrands.fulfilled, (state, action) => {
            state.brands = action.payload.data;
        });

        builder.addCase(postBrand.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(postBrand.fulfilled, (state, action) => {
            state.brands.push(action.payload.data);
            toast.success(`${t("Brand.AddBrandSuccess")}`);
        });

        builder.addCase(putBrand.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(putBrand.fulfilled, (state, action) => {
            state.brands = state.brands.map((brand) => {
                if (brand.id == action.payload.data.id) return action.payload.data;
                return brand;
            });
            toast.success(`${t("Brand.EditBrandSuccess")}`);
        });

        builder.addCase(deleteBrand.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(deleteBrand.fulfilled, (state, action) => {
            state.brands = state.brands.filter((brand) => brand.id != action.payload);
            toast.success(`${t("Brand.DeleteBrandSuccess")}`);
        });
    },
});

export default brandSlice.reducer;
